import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import JdBadge from "@components/JdBadge";
import React, { HTMLAttributes } from "react";
import { Column, Row } from "@src/components/Flex";
import FieldPreview from "./FieldPreview";
import { Geometry } from "ol/geom";
import Checkbox from "@components/Checkbox";

interface IProps extends Omit<HTMLAttributes<HTMLDivElement>, "id"> {
  id?: string | number;
  extra?: JSX.Element | null | "";
  title?: string;
  crop?: string;
  area?: string;
  harvestPercent?: number | null;
  harvestedArea?: string | null;
  /** Progress is still loading: a pulsing placeholder instead of "—". */
  harvestPending?: boolean;
  geometry?: Geometry;
  fillColor?: string;
  selected?: boolean;
  highlighted?: boolean;
  /** True when the field was imported from John Deere Operations Center. */
  isJohnDeereLinked?: boolean;
  /** True when the field was imported from Cropwise Operations. */
  isCropwiseLinked?: boolean;
  /** True when the user has force-marked this field (+season) as completed. */
  completed?: boolean;
  /** Show a selection checkbox at the left (bulk-edit mode). */
  selectable?: boolean;
  /** Checked state of the selection checkbox. */
  selectChecked?: boolean;
  /** Toggle handler for the selection checkbox. */
  onToggleSelect?: () => void;
}

const Root = styled.div<{ selected?: boolean, highlighted?: boolean }>`
  display: flex;
  align-items: stretch;
  padding: 12px 16px;
  box-sizing: border-box;
  border-top: 1px solid rgba(137, 144, 147, 0.3);
  border-right: 2px solid ${({ highlighted }) => (
    highlighted ? "rgb(104, 173, 24)" : "rgb(255, 255, 255)"
  )};
  min-height: 88px;
  background: ${({ highlighted, selected }) => {
    if (highlighted) {
      return selected ? "hsl(88 76% 90% / 1)" : "hsl(88 76% 95% / 1)";
    }
    return selected ? "#f8f8f8" : "#ffffff";
  }};
  transition: 0.4s;
  position: relative;
  cursor: pointer;
  width: 100%;
  :hover {
    background: #f8f8f8;
  }

  & > .more {
    position: absolute;
    inset: 8px 8px auto auto;
    cursor: pointer;
  }
`;

// Tick box pinned to the right edge of the card during bulk-select mode.
// Generous touch target (44px) so it's comfortable on mobile.
const CheckboxSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  min-width: 44px;
  padding-left: 8px;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const FieldName = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #494c5b;
`;


/** Cropwise counterpart to JdBadge. Same shape, different hue so the two are
 *  distinguishable at a glance when an account imports from both. */
const CwBadge = styled(JdBadge)`
  color: #1A4D8F;
  background: #E8F0FE;
`;

/** Small round green checkmark shown next to the field name when the user has
 *  force-confirmed the field as harvested. Sits right after <JdBadge> so the two
 *  coexist side-by-side, or the checkmark stands alone when the field isn't
 *  imported. Purely a visual signal — the harvest % stays factual. */
const CompletedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 6px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #68ad18;
  color: #ffffff;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const FieldData = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #899093;
`;

const ProgressLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  color: #494c5b;
  white-space: nowrap;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.45; }
  100% { opacity: 1; }
`;

/** Stands in for the "27% (19.26 ha)" label while progress loads. */
const ProgressLabelPending = styled.span`
  display: inline-block;
  width: 72px;
  height: 12px;
  margin: 3px 0;
  border-radius: 3px;
  background: #e8e8e8;
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const ProgressBarTrack = styled.div<{ pending?: boolean }>`
  width: 100%;
  height: 4px;
  background: #e8e8e8;
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
  ${({ pending }) =>
    pending &&
    css`
      animation: ${pulse} 1.2s ease-in-out infinite;
    `}
`;

const ProgressBarFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${({ percent }) => Math.min(percent, 100)}%;
  background: #68ad18;
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const FieldRow: React.FC<IProps> = ({
  id,
  title,
  crop,
  extra,
  area,
  harvestPercent,
  harvestedArea,
  harvestPending,
  geometry,
  fillColor,
  isJohnDeereLinked,
  isCropwiseLinked,
  completed,
  selectable,
  selectChecked,
  onToggleSelect,
  ...rest
}) => {
  const hasHarvestData = harvestPercent != null;
  const pct = Math.round((harvestPercent ?? 0) * 100);

  const dataParts = [crop, area].filter(Boolean);
  const fieldDataText = dataParts.join(" • ");

  const progressLabel = hasHarvestData
    ? `${pct}%${harvestedArea != null ? ` (${harvestedArea})` : ""}`
    : "—";

  return (
    <Root {...rest}>
      <FieldPreview id={id} geometry={geometry} fillColor={fillColor} />
      <Column
        justifyContent="space-between"
        crossAxisSize="max"
        mainAxisSize="stretch"
        style={{ gap: 4, minWidth: 0, flex: "1 1 0%", alignSelf: "stretch" }}
      >
        {/* Row 1: Field name - ellipsis with native tooltip on hover for full name */}
        <Row alignItems="center" mainAxisSize="stretch" style={{ minWidth: 0 }}>
          <FieldName title={title ?? undefined}>{title ?? ""}</FieldName>
          {isJohnDeereLinked && (
            <JdBadge title="Imported from John Deere Operations Center">JD</JdBadge>
          )}
          {/* Not gated on the feature config: a field already imported should
              keep showing where it came from even if the flag is switched off. */}
          {isCropwiseLinked && (
            <CwBadge title="Imported from Cropwise Operations">CW</CwBadge>
          )}
          {completed && (
            <CompletedBadge title="Marked as completed">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CompletedBadge>
          )}
        </Row>

        {/* Row 2: Crop and area */}
        {fieldDataText ? (
          <FieldData>{fieldDataText}</FieldData>
        ) : null}

        {/* Row 3: Harvest progress + edit button - always visible */}
        <Row
          mainAxisSize="stretch"
          justifyContent="space-between"
          alignItems="center"
          style={{ marginTop: 2 }}
        >
          <Column crossAxisSize="max" mainAxisSize="stretch" style={{ gap: 4, minWidth: 0, flex: 1 }}>
            <Row mainAxisSize="stretch" justifyContent="space-between" alignItems="center">
              {harvestPending ? (
                <ProgressLabelPending aria-hidden />
              ) : (
                <ProgressLabel>{progressLabel}</ProgressLabel>
              )}
              {extra}
            </Row>
            <ProgressBarTrack pending={harvestPending}>
              {!harvestPending && <ProgressBarFill percent={pct} />}
            </ProgressBarTrack>
          </Column>
        </Row>
      </Column>
      {selectable && (
        <CheckboxSlot
          // Single handler: stop the click from also bubbling to the row's
          // onClick (which toggles too) — otherwise it double-toggles.
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
        >
          <Checkbox checked={selectChecked} />
        </CheckboxSlot>
      )}
    </Root>
  );
};
export default FieldRow;
